import React from "react";
import type { MigrationJob } from "../../../type";

interface Props {
  job: MigrationJob;
  /** Clock source — defaults to Date.now(). Passed in so tests / polling
   *  visuals can stay deterministic if needed. */
  now?: number;
}

/**
 * Compact progress cell for the Jobs table.
 *
 * Shows three layers depending on job status:
 *   1. Progress bar (always rendered — 0% for pending/scheduled, 100% for
 *      terminal states, live % for running).
 *   2. Primary label: "67% · 6,600 / 9,900" (or "— / 19,797" for pending).
 *   3. Secondary line: "~5m left" for RUNNING jobs when enough progress has
 *      landed to estimate; cascading metadata counter if the job inserted
 *      any; blank otherwise.
 *
 * ETA math is deliberately simple: linear extrapolation from the time the
 * worker started (``started_at``) to the current done / total ratio.
 * Copy throughput is roughly linear on PostgreSQL bulk_create, so this
 * is accurate enough for ops reassurance. We explicitly don't show ETA
 * before the job has completed at least a few rows — early-phase rate is
 * dominated by startup overhead and produces wildly pessimistic numbers.
 */
const MIN_DONE_FOR_ETA = 5;

const formatEtaSeconds = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  if (seconds < 60) return `~${Math.round(seconds)}s left`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `~${minutes}m left`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  return remMin === 0 ? `~${hours}h left` : `~${hours}h ${remMin}m left`;
};

const JobProgress: React.FC<Props> = ({ job, now = Date.now() }) => {
  const { done = 0, total = 0 } = job?.progress || {};
  const pct = total > 0 ? Math.min(100, (done / total) * 100) : 0;

  // Running jobs fill progressively; terminal ones show full (or empty for
  // canceled with nothing done). Non-running pre-run states stay at 0.
  const isRunning = job.status === "running";
  const isTerminal =
    job.status === "succeeded" ||
    job.status === "failed" ||
    job.status === "canceled";

  const barWidth = isTerminal ? 100 : pct;
  const barTone =
    job.status === "failed"
      ? "bg-red-500"
      : job.status === "canceled"
      ? "bg-yellow-500"
      : isRunning || isTerminal
      ? "bg-blue-600"
      : "bg-gray-300";

  // ETA for running jobs only. Requires a started_at timestamp and some
  // observable progress; otherwise render nothing.
  let etaLine: string | null = null;
  if (isRunning && job.started_at && done >= MIN_DONE_FOR_ETA && done < total) {
    const elapsedMs = now - new Date(job.started_at).getTime();
    if (elapsedMs > 0) {
      const remaining = total - done;
      const etaSec = (remaining * elapsedMs) / done / 1000;
      etaLine = formatEtaSeconds(etaSec);
    }
  }

  return (
    <div className="min-w-[10rem]">
      <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${barTone} transition-all duration-300`}
          style={{ width: `${barWidth}%` }}
        />
      </div>
      <div className="flex items-baseline gap-2 mt-1 text-xs text-gray-700">
        <span className="font-medium tabular-nums">{Math.round(pct)}%</span>
        <span className="tabular-nums text-gray-500">
          {done.toLocaleString()} / {total.toLocaleString()}
        </span>
      </div>
      <div className="text-[10px] text-gray-500 leading-none">
        {etaLine}
        {etaLine && job.cascading_metadata_inserted > 0 && " · "}
        {job.cascading_metadata_inserted > 0 &&
          `+${job.cascading_metadata_inserted} meta`}
      </div>
    </div>
  );
};

export default JobProgress;
